import { FC, ReactNode } from 'react';
import { cn } from '../../utils/cn';
import { Header } from './Header';
import { Footer } from './Footer';
import { SkipLink } from '../a11y';

export interface LayoutProps {
  /**
   * Page content
   */
  children: ReactNode;
  /**
   * Whether to show the header
   * @default true
   */
  showHeader?: boolean;
  /**
   * Whether to show the footer
   * @default true
   */
  showFooter?: boolean;
  /**
   * Whether to add top padding for the fixed header
   * Set to false for pages with hero sections that extend behind the header
   * @default true
   */
  hasHeaderPadding?: boolean;
  /**
   * Custom class name for the main content area
   */
  className?: string;
  /**
   * Custom class name for the outer wrapper
   */
  wrapperClassName?: string;
}

/**
 * Layout component - Main layout wrapper with Header and Footer
 * Provides consistent structure across all pages
 *
 * @example
 * // Standard page with header padding
 * <Layout>
 *   <PageContent />
 * </Layout>
 *
 * @example
 * // Hero page without header padding (content extends behind transparent header)
 * <Layout hasHeaderPadding={false}>
 *   <HeroSection />
 *   <OtherContent />
 * </Layout>
 *
 * @example
 * // Page without footer
 * <Layout showFooter={false}>
 *   <CheckoutPage />
 * </Layout>
 */
export const Layout: FC<LayoutProps> = ({
  children,
  showHeader = true,
  showFooter = true,
  hasHeaderPadding = true,
  className,
  wrapperClassName,
}) => {
  return (
    <div className={cn('flex flex-col min-h-screen', wrapperClassName)}>
      {/* Skip link for keyboard navigation - WCAG 2.4.1 (Bypass Blocks) */}
      <SkipLink targetId="main-content" />

      {showHeader && (
        <header role="banner">
          <Header />
        </header>
      )}

      <main
        id="main-content"
        tabIndex={-1}
        className={cn(
          'flex-1',
          // Add padding for fixed header unless disabled (e.g., for hero sections)
          showHeader && hasHeaderPadding && 'pt-16',
          className
        )}
      >
        {children}
      </main>

      {showFooter && (
        <footer role="contentinfo">
          <Footer />
        </footer>
      )}
    </div>
  );
};

Layout.displayName = 'Layout';
